/*
 * Figma `95:2` — Select. State: Default, Focus, Error, Disabled, Placeholder.
 *
 * A native `<select>`, deliberately. The console runs on venue tablets and staff phones as well as
 * desktops, and the platform's own picker is the one that already works with a screen reader, a
 * hardware keyboard and a thumb. A custom listbox would have to re-earn all three, and the frozen
 * design asks for no behaviour the native control lacks.
 *
 * The error contract is `TextField`'s, because a form that announces one field's rejection one way
 * and the next field's another way is a form nobody can learn: `aria-invalid`, a described-by
 * pointing at the reason, and a visible sentence with a glyph.
 */
import { useId } from 'react';
import type { ReactNode, SelectHTMLAttributes } from 'react';
import { IconWarning, IconChevronDown } from './icons';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id' | 'children'> {
  label: string;
  options: readonly SelectOption[];
  /** The message shown when the value is rejected. Its presence *is* the error state. */
  error?: string | null;
  hint?: string;
  /**
   * Shown as a disabled first option while nothing is chosen. Give one only where "nothing chosen"
   * is a real state the user must resolve — never as a label for the field.
   */
  placeholder?: string;
  requiredMark?: boolean;
  trailing?: ReactNode;
}

export function Select({
  label, options, error, hint, placeholder, requiredMark, trailing, className = '', value, ...rest
}: SelectProps) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ') || undefined;

  return (
    <div className="jt-field">
      <label className="jt-field-label" htmlFor={id}>
        {label}
        {requiredMark && <span className="jt-field-required" aria-hidden="true">*</span>}
      </label>
      <div style={{ display: 'flex', gap: 'var(--jt-space-2)', alignItems: 'center' }}>
        <div className="jt-select-wrap">
          <select
            {...rest}
            id={id}
            value={value}
            className={`jt-input jt-select ${className}`.trim()}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
          >
            {placeholder && <option value="" disabled>{placeholder}</option>}
            {options.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
          </select>
          <span className="jt-select-chevron"><IconChevronDown size={16} /></span>
        </div>
        {trailing}
      </div>
      {hint && <div className="jt-field-hint" id={hintId}>{hint}</div>}
      {error && (
        <div className="jt-field-error" id={errorId}>
          <IconWarning size={14} />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}
